/**
 * The first-login welcome turn, as a plain function.
 *
 * ─── Why this is not just a hook ────────────────────────────────────────────
 * WHICH greeting is correct depends on a fact that is not known when the wizard
 * mounts: whether the account already has a sales channel. Signing up with
 * Mercado Libre connects the channel and starts the catalog import during that
 * first sign-in, so greeting such a user with "¿en qué canal estás vendiendo
 * hoy?" and a channel picker asks about something already done.
 *
 * That fact arrives with the integrations subscription's first snapshot, which
 * is owned by `onboardingWizard`'s `connectionWatchPolicy`. A policy cannot call
 * a React hook, so the seeding lives here — one function, callable from both —
 * and the DECISION lives in the one place that has the data.
 *
 * The `messages.length > 0` guard is what keeps the two callers from stacking
 * greetings, and what makes a remount or a second gate evaluation a no-op.
 */

import { getAIAssistantState, dispatchAIAssistantCommand } from './store';

/**
 * Copy for the first-login welcome turn. Prose de producto, sin emojis: es lo
 * primero que la cuenta ve del asistente.
 */
const ONBOARDING_GREETING = [
  'Hola, soy Tessy. Antes de recorrer la app conviene traer lo que ya vendés — ' +
  'así todo lo que configuremos después se apoya en tus datos reales y no en un ' +
  'catálogo vacío.',
  '¿En qué canal estás vendiendo hoy?'
].join( '\n\n' );

/** True when the conversation is still empty, i.e. nothing has been seeded. */
export const conversationIsEmpty = (): boolean =>
  getAIAssistantState().messages.length === 0;

/**
 * Seed the welcome turn carrying the reusable `channel-picker` block.
 *
 * Dispatched as a local `addAssistant` rather than a backend round-trip — the
 * greeting is deterministic app copy, and making the user wait on the LLM to be
 * told "hola" is the wrong first impression. Everything after this turn is a
 * normal conversation.
 */
export const seedChannelPickerGreeting = (): void => {
  if ( !conversationIsEmpty()) return;

  const timestamp = Date.now();
  dispatchAIAssistantCommand(
    {
      type: 'aiAssistant/message/addAssistant',
      payload: {
        id: `msg-onboarding-${timestamp}`,
        timestamp,
        content: ONBOARDING_GREETING,
        block: { type: 'channel-picker', payload: {}}
      }
    },
    { source: 'application' }
  );
};
